import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaSitemap,
  FaSync,
  FaExclamationTriangle,
  FaChevronDown,
  FaSearch,
  FaUsers,
  FaLayerGroup,
  FaBuilding,
} from "react-icons/fa";
import api from "../api";
import "./OrganizationChart.css";

function buildHierarchy(nodes, edges) {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const childrenMap = new Map();
  const supervisorMap = new Map();

  edges.forEach(({ supervisorId, userId }) => {
    if (!nodeMap.has(supervisorId) || !nodeMap.has(userId)) {
      return;
    }
    if (!childrenMap.has(supervisorId)) {
      childrenMap.set(supervisorId, []);
    }
    childrenMap.get(supervisorId).push(userId);

    if (!supervisorMap.has(userId)) {
      supervisorMap.set(userId, []);
    }
    supervisorMap.get(userId).push(supervisorId);
  });

  const roots = nodes.filter((node) => !supervisorMap.has(node.id));
  const rootSource = roots.length ? roots : nodes;
  const rootIds = rootSource
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((node) => node.id);

  return { nodeMap, childrenMap, rootIds };
}

function computeDescendantCounts(maps) {
  const counts = new Map();
  const visiting = new Set();

  const compute = (id) => {
    if (counts.has(id)) {
      return counts.get(id);
    }
    if (visiting.has(id)) {
      counts.set(id, 0);
      return 0;
    }
    visiting.add(id);
    const children = maps.childrenMap.get(id) || [];
    let total = 0;
    for (const child of children) {
      total += 1;
      total += compute(child);
    }
    visiting.delete(id);
    counts.set(id, total);
    return total;
  };

  maps.nodeMap.forEach((_, id) => {
    compute(id);
  });

  return counts;
}

function filterVisibleNodes(maps, term) {
  if (!term) {
    return { rootIds: maps.rootIds, visibleNodes: null };
  }

  const normalized = term.toLowerCase();
  const visibleNodes = new Set();
  const cache = new Map();
  const visiting = new Set();

  const matchesNode = (id) => {
    if (cache.has(id)) {
      return cache.get(id);
    }
    if (visiting.has(id)) {
      cache.set(id, false);
      return false;
    }
    visiting.add(id);

    const node = maps.nodeMap.get(id);
    if (!node) {
      visiting.delete(id);
      cache.set(id, false);
      return false;
    }

    const sources = [node.name, node.email, node.group?.name, node.sector?.name];
    const selfMatches = sources.some(
      (value) => typeof value === "string" && value.toLowerCase().includes(normalized),
    );

    const children = maps.childrenMap.get(id) || [];
    let childMatches = false;
    for (const child of children) {
      if (matchesNode(child)) {
        childMatches = true;
      }
    }

    visiting.delete(id);
    const result = selfMatches || childMatches;
    if (result) {
      visibleNodes.add(id);
    }
    cache.set(id, result);
    return result;
  };

  const rootIds = maps.rootIds.filter((id) => matchesNode(id));
  return { rootIds, visibleNodes };
}

function getDepthTheme(depth) {
  const themes = [
    {
      card: "org-card-primary",
      badge: "org-badge-primary",
      toggle: "org-toggle-primary",
    },
    {
      card: "org-card-secondary",
      badge: "org-badge-secondary",
      toggle: "org-toggle-secondary",
    },
    {
      card: "org-card-tertiary",
      badge: "org-badge-tertiary",
      toggle: "org-toggle-tertiary",
    },
  ];

  if (depth <= 1) {
    return themes[depth];
  }
  return themes[themes.length - 1];
}

function TreeNode({
  nodeId,
  maps,
  collapsedNodes,
  toggleNode,
  descendantCounts,
  visibleNodes,
  searchTerm,
  depth = 0,
  trail = new Set(),
}) {
  if (!maps.nodeMap.has(nodeId)) {
    return null;
  }

  if (visibleNodes && !visibleNodes.has(nodeId)) {
    return null;
  }

  const node = maps.nodeMap.get(nodeId);
  const children = maps.childrenMap.get(nodeId) || [];
  const pathKey = `${nodeId}`;
  const isCycle = trail.has(pathKey);
  const hasChildren = children.length > 0 && !isCycle;
  const isCollapsed = collapsedNodes.has(nodeId);
  const showChildren = hasChildren && !isCollapsed;
  const nextTrail = new Set(trail);
  nextTrail.add(pathKey);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesTerm = normalizedSearch
    ? [node.name, node.email, node.group?.name, node.sector?.name].some(
        (value) => typeof value === "string" && value.toLowerCase().includes(normalizedSearch),
      )
    : false;

  const descendantTotal = descendantCounts.get(nodeId) || 0;
  const sortedChildren = children
    .slice()
    .sort((a, b) => {
      const nameA = maps.nodeMap.get(a)?.name || "";
      const nameB = maps.nodeMap.get(b)?.name || "";
      return nameA.localeCompare(nameB);
    });

  const theme = getDepthTheme(depth);
  const childCount = sortedChildren.length;

  return (
    <div className="org-chart-node">
      <div
        className={`org-chart-card ${theme.card} ${
          matchesTerm ? "ring-4 ring-emerald-300/40" : ""
        } ${isCollapsed && hasChildren ? "opacity-90" : ""}`}
      >
        <div className="org-chart-details">
          <h3 className="org-chart-name">{node.name}</h3>
          <p className="org-chart-email">{node.email || "Sem e-mail"}</p>
        </div>
        <div className="org-chart-tags">
          {node.group?.name && (
            <span className={`org-chart-tag ${theme.badge}`}>
              <FaLayerGroup />
              {node.group.name}
            </span>
          )}
          {node.sector?.name && (
            <span className={`org-chart-tag ${theme.badge}`}>
              <FaBuilding />
              {node.sector.name}
            </span>
          )}
          <span className={`org-chart-tag ${theme.badge}`}>
            <FaUsers />
            {descendantTotal} subordinado{descendantTotal === 1 ? "" : "s"}
          </span>
          {isCycle && (
            <span className="org-chart-tag org-tag-warning">Relação cíclica detectada</span>
          )}
        </div>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => toggleNode(nodeId)}
            className={`org-chart-toggle ${theme.toggle}`}
            aria-expanded={!isCollapsed}
            aria-label={`${isCollapsed ? "Expandir" : "Recolher"} subordinados de ${node.name}`}
          >
            <FaChevronDown className={`transition ${isCollapsed ? "" : "rotate-180"}`} />
            {isCollapsed ? "Expandir" : "Recolher"}
          </button>
        ) : null}
      </div>

      {showChildren ? (
        <div
          className={`org-chart-children ${childCount <= 1 ? "org-chart-children-single" : ""}`}
        >
          {sortedChildren.map((childId) => (
            <div key={`${nodeId}-${childId}`} className="org-chart-child">
              <TreeNode
                nodeId={childId}
                maps={maps}
                collapsedNodes={collapsedNodes}
                toggleNode={toggleNode}
                descendantCounts={descendantCounts}
                visibleNodes={visibleNodes}
                searchTerm={searchTerm}
                depth={depth + 1}
                trail={nextTrail}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function OrganizationChart({ refreshKey }) {
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const fetchGraph = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/users/hierarchy");
      const nodes = Array.isArray(response.data?.nodes) ? response.data.nodes : [];
      const edges = Array.isArray(response.data?.edges) ? response.data.edges : [];
      setGraph({ nodes, edges });
    } catch (err) {
      const message = err.response?.data?.error || "Não foi possível carregar o organograma.";
      setError(message);
      setGraph({ nodes: [], edges: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGraph();
  }, [fetchGraph, refreshKey]);

  const maps = useMemo(() => buildHierarchy(graph.nodes, graph.edges), [graph.nodes, graph.edges]);
  const hasNodes = maps.nodeMap.size > 0;

  const descendantCounts = useMemo(() => computeDescendantCounts(maps), [maps]);

  const stats = useMemo(() => {
    const groups = new Set();
    const sectors = new Set();
    maps.nodeMap.forEach((node) => {
      if (node.group?.name) {
        groups.add(node.group.name);
      }
      if (node.sector?.name) {
        sectors.add(node.sector.name);
      }
    });
    return {
      collaborators: maps.nodeMap.size,
      groups: groups.size,
      sectors: sectors.size,
      roots: maps.rootIds.length,
    };
  }, [maps]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const { rootIds: filteredRootIds, visibleNodes } = useMemo(
    () => filterVisibleNodes(maps, normalizedSearch),
    [maps, normalizedSearch],
  );

  useEffect(() => {
    if (!normalizedSearch || !visibleNodes) {
      return;
    }
    setCollapsedNodes((prev) => {
      let changed = false;
      const next = new Set(prev);
      visibleNodes.forEach((id) => {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [normalizedSearch, visibleNodes]);

  const toggleNode = useCallback((id) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setCollapsedNodes(() => new Set());
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsedNodes(() => {
      const next = new Set();
      maps.childrenMap.forEach((childList, id) => {
        if (Array.isArray(childList) && childList.length) {
          next.add(id);
        }
      });
      return next;
    });
  }, [maps]);

  const rootIdsToRender = normalizedSearch ? filteredRootIds : maps.rootIds;
  const hasSearchResults = !normalizedSearch || rootIdsToRender.length > 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Estrutura</p>
            <h1 className="text-3xl font-semibold text-slate-900">Organograma</h1>
          </div>
        </header>
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
          Carregando organograma...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Estrutura</p>
            <h1 className="text-3xl font-semibold text-slate-900">Organograma</h1>
          </div>
          <button
            type="button"
            onClick={fetchGraph}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50"
          >
            <FaSync className="text-emerald-500" />
            Recarregar
          </button>
        </header>
        <div className="flex items-center gap-3 rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <FaExclamationTriangle className="text-xl" />
          <div>
            <p className="font-semibold">Não foi possível carregar o organograma.</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Estrutura</p>
          <h1 className="text-3xl font-semibold text-slate-900">Organograma</h1>
          <p className="text-sm text-slate-500">
            Visualize a hierarquia completa da empresa, identifique equipes e encontre rapidamente cada colaborador.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchGraph}
          className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50"
        >
          <FaSync />
          Atualizar
        </button>
      </header>

      {hasNodes ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                key: "collaborators",
                label: "Colaboradores",
                value: stats.collaborators,
                icon: FaUsers,
                iconClasses: "bg-emerald-100 text-emerald-600",
              },
              {
                key: "roots",
                label: "Equipes principais",
                value: stats.roots,
                icon: FaSitemap,
                iconClasses: "bg-sky-100 text-sky-600",
              },
              {
                key: "groups",
                label: "Grupos",
                value: stats.groups,
                icon: FaLayerGroup,
                iconClasses: "bg-violet-100 text-violet-600",
              },
              {
                key: "sectors",
                label: "Setores",
                value: stats.sectors,
                icon: FaBuilding,
                iconClasses: "bg-amber-100 text-amber-600",
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.key} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div
                    className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${card.iconClasses}`}
                  >
                    <Icon />
                  </div>
                  <p className="text-sm text-slate-500">{card.label}</p>
                  <p className="text-2xl font-semibold text-slate-900">{card.value}</p>
                </div>
              );
            })}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-sm">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por nome, setor ou grupo"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  aria-label="Buscar no organograma"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={expandAll}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                >
                  Expandir tudo
                </button>
                <button
                  type="button"
                  onClick={collapseAll}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                >
                  Recolher tudo
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Utilize a busca para localizar rapidamente pessoas específicas ou limitar a visualização por grupo e setor.
            </p>
          </div>

          {hasSearchResults ? (
            <div className="space-y-6">
              {rootIdsToRender.map((id) => (
                <TreeNode
                  key={id}
                  nodeId={id}
                  maps={maps}
                  collapsedNodes={collapsedNodes}
                  toggleNode={toggleNode}
                  descendantCounts={descendantCounts}
                  visibleNodes={visibleNodes}
                  searchTerm={searchTerm}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
              Nenhum resultado encontrado para "{searchTerm}".
            </div>
          )}
        </>
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500">
          Nenhuma informação de hierarquia disponível.
        </div>
      )}
    </div>
  );
}
